'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useQuery, useAction } from 'convex/react'
import { useOrganization, useOrganizationList } from '@clerk/nextjs'
import useHardSignOut from '@/hooks/useHardSignOut'
import {
  HomeIcon,
  ChartBarIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  BeakerIcon,
  DocumentChartBarIcon,
  HeartIcon,
  Square3Stack3DIcon,
  Squares2X2Icon,
  GlobeAltIcon,
  TagIcon,
  ChatBubbleLeftRightIcon,
  LinkIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UsersIcon,
  TicketIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ArrowLeftOnRectangleIcon,
  ArrowsRightLeftIcon,
  SwatchIcon,
  PlusIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ChevronLeftIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'
import { api } from '../../convex/_generated/api'
import useOrganizationPermissions, { FEATURE } from '@/hooks/useOrganizationPermissions'
import useUserPermissions from '@/hooks/useUserPermissions'
import { useTheme } from './ThemeProvider'
// Admin website-first creation modal — same one as /admin/organizations;
// operator-gated backend, so the create-org command gates on isOperator.
import CreateOrganizationModal from '@/app/admin/organizations/CreateOrganizationModal'

type IconType = React.ComponentType<{ className?: string; style?: React.CSSProperties }>

interface PageNavItem {
  nameKey: string
  groupKey: string
  href: string
  icon: IconType
  lockedFeature?: keyof typeof FEATURE | null
}

const PAGE_ITEMS: PageNavItem[] = [
  { nameKey: 'pages.dashboard', groupKey: 'pageGroups.brandVisibility', href: '/dashboard', icon: HomeIcon },
  { nameKey: 'pages.brands', groupKey: 'pageGroups.brandVisibility', href: '/brands', icon: BuildingOfficeIcon },
  { nameKey: 'pages.reports', groupKey: 'pageGroups.brandVisibility', href: '/reports', icon: DocumentChartBarIcon, lockedFeature: 'WEEKLY_REPORTS' },
  { nameKey: 'pages.citations', groupKey: 'pageGroups.brandVisibility', href: '/citations', icon: ChartBarIcon },
  { nameKey: 'pages.projects', groupKey: 'pageGroups.analysisEngine', href: '/analysis', icon: BeakerIcon },
  { nameKey: 'pages.prompts', groupKey: 'pageGroups.analysisEngine', href: '/templates', icon: DocumentTextIcon },
  { nameKey: 'pages.verticals', groupKey: 'pageGroups.analysisEngine', href: '/verticals', icon: TagIcon, lockedFeature: 'PUBLISHER_TOPIC_INSIGHTS' },
  { nameKey: 'pages.sources', groupKey: 'pageGroups.analysisEngine', href: '/sources', icon: LinkIcon, lockedFeature: 'PUBLISHER_TOPIC_INSIGHTS' },
  { nameKey: 'pages.domainOverview', groupKey: 'pageGroups.domainImpact', href: '/publisher', icon: Squares2X2Icon, lockedFeature: 'VITALS_PAGE' },
  { nameKey: 'pages.content', groupKey: 'pageGroups.domainImpact', href: '/content', icon: DocumentTextIcon },
  { nameKey: 'pages.contentPages', groupKey: 'pageGroups.domainImpact', href: '/content/pages', icon: DocumentTextIcon, lockedFeature: 'PUBLISHER_TOPIC_INSIGHTS' },
  { nameKey: 'pages.contentOpportunities', groupKey: 'pageGroups.domainImpact', href: '/content/opportunities', icon: ExclamationTriangleIcon, lockedFeature: 'PUBLISHER_TOPIC_INSIGHTS' },
  { nameKey: 'pages.mentions', groupKey: 'pageGroups.domainImpact', href: '/mentions', icon: ChatBubbleLeftRightIcon },
  { nameKey: 'pages.vitals', groupKey: 'pageGroups.domainImpact', href: '/vitals', icon: HeartIcon, lockedFeature: 'VITALS_PAGE' },
  { nameKey: 'pages.traffic', groupKey: 'pageGroups.connectivity', href: '/traffic', icon: GlobeAltIcon, lockedFeature: 'INTEGRATIONS' },
  { nameKey: 'pages.integrations', groupKey: 'pageGroups.connectivity', href: '/integration-hub', icon: Square3Stack3DIcon, lockedFeature: 'INTEGRATIONS' },
]

const ADMIN_ITEMS: { nameKey: string; href: string; icon: IconType }[] = [
  { nameKey: 'admin.overview', href: '/admin', icon: ShieldCheckIcon },
  { nameKey: 'admin.organizations', href: '/admin/organizations', icon: BuildingOfficeIcon },
  { nameKey: 'admin.users', href: '/admin/users', icon: UsersIcon },
  { nameKey: 'admin.projects', href: '/admin/analysis', icon: BeakerIcon },
  { nameKey: 'admin.usage', href: '/admin/usage', icon: ChartBarIcon },
  { nameKey: 'admin.referrals', href: '/admin/referrals', icon: TicketIcon },
  { nameKey: 'admin.plans', href: '/admin/plans', icon: CurrencyDollarIcon },
  { nameKey: 'admin.topics', href: '/admin/topics', icon: TagIcon },
  { nameKey: 'admin.partners', href: '/admin/partners', icon: UserGroupIcon },
]

type ResultItem = {
  id: string
  group: string
  name: string
  subtitle?: string
  icon: IconType
} & (
  | { href: string; action?: never }
  // Action items navigate via a callback (e.g. opening an external billing
  // portal in a new tab). The palette closes after invoking the action.
  | { action: () => void; href?: never }
)

function fuzzyMatch(haystack: string, needle: string): boolean {
  if (!needle) return true
  const h = haystack.toLowerCase()
  const n = needle.toLowerCase().trim()
  if (h.includes(n)) return true
  let i = 0
  for (const ch of h) {
    if (ch === n[i]) i++
    if (i === n.length) return true
  }
  return i === n.length
}

// Cap the per-section result count for prompts and analysis projects. Beyond
// this, the user is expected to refine their search instead of scrolling.
const SEARCH_LIMIT = 25
// Wait this long after the last keystroke before re-querying the server.
const SEARCH_DEBOUNCE_MS = 150

// Subviews open from the Actions group. The main view shows search results;
// the others present a focused list (org switcher, theme picker).
type PaletteView = 'main' | 'orgs' | 'themes'

export default function CommandPalette() {
  const t = useTranslations('account.commandPalette')
  const [isOpen, setIsOpen] = useState(false)
  const [view, setView] = useState<PaletteView>('main')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const { has } = useOrganizationPermissions()
  const { isAdmin, isOperator, isPartnerUser } = useUserPermissions()
  const organizations = useQuery(api.organizations.getOrganizations)
  const organizationId = organizations?.[0]?._id
  const hasPaymentOverride = organizations?.[0]?.paymentOverride != null

  // Mirror the gating in UserMenu: Settings is available to org admins of the
  // active org and to 42a admins (including impersonating operators).
  const { organization: activeOrg } = useOrganization()
  const { userMemberships, setActive } = useOrganizationList({
    userMemberships: { infinite: true, pageSize: 10 },
  })
  const isOrgAdmin =
    !!activeOrg &&
    userMemberships.data?.find((m) => m.organization.id === activeOrg.id)
      ?.role === 'org:admin'
  const canAccessSettings = isOrgAdmin || isAdmin === true

  const hardSignOut = useHardSignOut()
  const { theme, setTheme } = useTheme()

  const getBillingPortalUrl = useAction(
    api.features.billing.lsActions.getBillingPortalUrl,
  )
  const openBillingPortal = async () => {
    setIsOpen(false)
    const newTab = window.open('', '_blank')
    try {
      const { portalUrl } = await getBillingPortalUrl({})
      if (newTab) newTab.location.href = portalUrl
      else window.open(portalUrl, '_blank', 'noopener,noreferrer')
    } catch (err) {
      newTab?.close()
      console.error('Failed to open billing portal', err)
    }
  }

  // Don't fire the entity queries until the user actually opens the palette —
  // the convex queries are paginated/limited but there's no point loading them
  // for users who never press ⌘K. Submenu views also skip them.
  const entityQueryArgs =
    isOpen && view === 'main' && organizationId
      ? { organizationId, search: debouncedQuery, limit: SEARCH_LIMIT }
      : 'skip'

  const analysisConfigs = useQuery(
    api.analysisConfig.searchAnalysisConfigs,
    entityQueryArgs,
  )
  const promptTemplates = useQuery(
    api.analysis.prompts.searchPromptTemplates,
    entityQueryArgs,
  )

  // Toggle on CMD+K / Ctrl+K. Escape backs out of a subview, or closes from
  // the main view.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setIsOpen((v) => !v)
      } else if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        if (view !== 'main') setView('main')
        else setIsOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, view])

  // Reset state when opening, focus the input.
  useEffect(() => {
    if (isOpen) {
      setView('main')
      setQuery('')
      setDebouncedQuery('')
      setActiveIndex(0)
      // Focus after the dialog has mounted.
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [isOpen])

  // Reset query + selection on view change so each subview starts fresh.
  useEffect(() => {
    setQuery('')
    setDebouncedQuery('')
    setActiveIndex(0)
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [view])

  // Debounce the search before re-querying convex.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [query])

  const results = useMemo<ResultItem[]>(() => {
    const items: ResultItem[] = []

    // ── Org switcher subview ────────────────────────────────────────────
    if (view === 'orgs') {
      const memberships = userMemberships.data ?? []
      for (const m of memberships) {
        if (!fuzzyMatch(m.organization.name, query)) continue
        const isCurrent = activeOrg?.id === m.organization.id
        items.push({
          id: `org:${m.organization.id}`,
          group: t('groupSwitchOrg'),
          name: m.organization.name,
          subtitle: isCurrent ? t('current') : undefined,
          icon: BuildingOfficeIcon,
          action: async () => {
            if (!setActive) return
            setIsOpen(false)
            await setActive({ organization: m.organization.id })
          },
        })
      }
      return items
    }

    // ── Theme subview ───────────────────────────────────────────────────
    if (view === 'themes') {
      const themeChoices: { id: 'light' | 'dark' | 'system'; nameKey: string; icon: IconType }[] = [
        { id: 'light', nameKey: 'themes.light', icon: SunIcon },
        { id: 'dark', nameKey: 'themes.dark', icon: MoonIcon },
        { id: 'system', nameKey: 'themes.system', icon: ComputerDesktopIcon },
      ]
      for (const tc of themeChoices) {
        const tcName = t(tc.nameKey)
        if (!fuzzyMatch(tcName, query)) continue
        items.push({
          id: `theme:${tc.id}`,
          group: t('groupChangeTheme'),
          name: tcName,
          subtitle: theme === tc.id ? t('current') : undefined,
          icon: tc.icon,
          action: () => {
            setTheme(tc.id)
            setIsOpen(false)
          },
        })
      }
      return items
    }

    // ── Main view ───────────────────────────────────────────────────────
    for (const p of PAGE_ITEMS) {
      const locked = p.lockedFeature
        ? !has({ feature: FEATURE[p.lockedFeature] })
        : false
      if (locked) continue
      const pName = t(p.nameKey)
      const pGroup = t(p.groupKey)
      if (!fuzzyMatch(`${pName} ${pGroup}`, query)) continue
      items.push({
        id: `page:${p.href}`,
        group: `${t('pages.groupPrefix')}${pGroup}`,
        name: pName,
        href: p.href,
        icon: p.icon,
      })
    }

    // Items mirroring the user menu (profile, settings, billing, partner
    // portal). Each entry is gated by the same predicate as in UserMenu so
    // we don't surface destinations the user can't actually open.
    type UserMenuItem = {
      id: string
      name: string
      icon: IconType
      keywords?: string
    } & ({ href: string; action?: never } | { action: () => void; href?: never })

    const userMenuItems: UserMenuItem[] = [
      { id: 'user:profile', name: t('userMenuItems.profile'), icon: UserCircleIcon, href: '/profile' },
    ]
    if (!hasPaymentOverride) {
      userMenuItems.push({
        id: 'user:billing',
        name: t('userMenuItems.billing'),
        icon: CreditCardIcon,
        action: openBillingPortal,
        keywords: 'subscription invoice payment',
      })
    }
    if (canAccessSettings) {
      userMenuItems.push({
        id: 'user:settings',
        name: t('userMenuItems.settings'),
        icon: Cog6ToothIcon,
        href: '/settings',
      })
    }
    if (isPartnerUser) {
      userMenuItems.push(
        { id: 'partner:overview', name: t('userMenuItems.partnerPortal'), icon: UserGroupIcon, href: '/partners', keywords: 'partner organizations' },
        { id: 'partner:team', name: t('userMenuItems.partnerTeam'), icon: UsersIcon, href: '/partners/team' },
        { id: 'partner:referrals', name: t('userMenuItems.partnerReferrals'), icon: TicketIcon, href: '/partners/referrals' },
      )
    }
    for (const u of userMenuItems) {
      if (!fuzzyMatch(`${u.name} ${u.keywords ?? ''}`, query)) continue
      items.push({
        id: u.id,
        group: t('groupAccount'),
        name: u.name,
        icon: u.icon,
        ...('href' in u && u.href !== undefined
          ? { href: u.href }
          : { action: u.action! }),
      } as ResultItem)
    }

    // Actions: things that aren't navigations — submenus or one-shot
    // operations. The org switcher and theme picker push us into a subview;
    // sign-out and create-org execute immediately.
    type ActionItem = {
      id: string
      name: string
      icon: IconType
      action: () => void
      keywords?: string
    }
    const actionItems: ActionItem[] = []
    if ((userMemberships.data?.length ?? 0) > 1) {
      actionItems.push({
        id: 'action:switch-org',
        name: t('actions.switchOrganization'),
        icon: ArrowsRightLeftIcon,
        action: () => setView('orgs'),
        keywords: 'change organization workspace',
      })
    }
    actionItems.push({
      id: 'action:theme',
      name: t('actions.changeTheme'),
      icon: SwatchIcon,
      action: () => setView('themes'),
      keywords: 'dark light appearance',
    })
    if (isOperator) {
      actionItems.push({
        id: 'action:create-org',
        name: t('actions.createOrganization'),
        icon: PlusIcon,
        action: () => {
          // Close the palette before opening the modal so the modal renders
          // unblocked by the search overlay.
          setIsOpen(false)
          setShowCreateOrgModal(true)
        },
        keywords: 'new organization workspace',
      })
    }
    actionItems.push({
      id: 'action:sign-out',
      name: t('actions.signOut'),
      icon: ArrowLeftOnRectangleIcon,
      action: () => hardSignOut(),
      keywords: 'logout log out',
    })
    for (const a of actionItems) {
      if (!fuzzyMatch(`${a.name} ${a.keywords ?? ''}`, query)) continue
      items.push({
        id: a.id,
        group: t('groupActions'),
        name: a.name,
        icon: a.icon,
        action: a.action,
      })
    }

    if (isAdmin) {
      for (const a of ADMIN_ITEMS) {
        const aName = t(a.nameKey)
        if (!fuzzyMatch(`${aName} admin`, query)) continue
        items.push({
          id: `admin:${a.href}`,
          group: t('groupAdmin'),
          name: aName,
          href: a.href,
          icon: a.icon,
        })
      }
    }

    // Entity searches (projects, prompts) come pre-filtered + limited by the
    // server, so we just append them in order.
    if (analysisConfigs) {
      for (const c of analysisConfigs) {
        items.push({
          id: `project:${c._id}`,
          group: t('groupProjects'),
          name: c.name,
          subtitle: c.description || undefined,
          href: `/analysis/${c._id}/overview`,
          icon: BeakerIcon,
        })
      }
    }

    if (promptTemplates) {
      for (const tmpl of promptTemplates) {
        items.push({
          id: `prompt:${tmpl._id}`,
          group: t('groupPrompts'),
          name: tmpl.name,
          subtitle: tmpl.contentPreview,
          href: `/templates/${tmpl._id}/overview`,
          icon: DocumentTextIcon,
        })
      }
    }

    return items
  }, [
    t,
    view,
    query,
    has,
    isAdmin,
    isOperator,
    isPartnerUser,
    canAccessSettings,
    hasPaymentOverride,
    openBillingPortal,
    activeOrg,
    userMemberships.data,
    setActive,
    theme,
    setTheme,
    hardSignOut,
    analysisConfigs,
    promptTemplates,
  ])

  // Reset active index when results change.
  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Keep active row visible.
  useEffect(() => {
    if (!listRef.current) return
    const el = listRef.current.querySelector<HTMLElement>(`[data-idx="${activeIndex}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const selectIndex = (i: number) => {
    const item = results[i]
    if (!item) return
    if (item.href !== undefined) {
      setIsOpen(false)
      router.push(item.href)
      return
    }
    // Action items manage their own close behavior — switching view stays
    // open, leaf actions close themselves.
    item.action()
  }

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(results.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      selectIndex(activeIndex)
    } else if (e.key === 'Home') {
      e.preventDefault()
      setActiveIndex(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      setActiveIndex(Math.max(0, results.length - 1))
    } else if (e.key === 'Backspace' && query === '' && view !== 'main') {
      // Empty-input Backspace in a subview → go back to main.
      e.preventDefault()
      setView('main')
    }
  }

  const placeholder =
    view === 'orgs'
      ? t('placeholderOrgs')
      : view === 'themes'
        ? t('placeholderThemes')
        : t('placeholderMain')

  // Group results so we can render dividers/headers while keeping a flat
  // active-index. We compute the running index inline during render.
  const grouped: { group: string; items: ResultItem[] }[] = []
  for (const r of results) {
    const last = grouped[grouped.length - 1]
    if (last && last.group === r.group) last.items.push(r)
    else grouped.push({ group: r.group, items: [r] })
  }

  let runningIndex = -1

  return (
    <>
      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
      />
      {isOpen && (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('ariaLabel')}
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4"
      onClick={() => setIsOpen(false)}
    >
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.55)' }}
      />
      <div
        className="relative w-full max-w-xl rounded-xl shadow-2xl overflow-hidden border"
        style={{
          background: 'var(--color-surface)',
          borderColor: 'var(--color-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center gap-3 px-4 py-3 border-b"
          style={{ borderColor: 'var(--color-border)' }}
        >
          {view === 'main' ? (
            <MagnifyingGlassIcon
              className="w-5 h-5"
              style={{ color: 'var(--color-text-muted)' }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setView('main')}
              aria-label={t('backToMain')}
              className="p-0.5 -m-0.5 rounded hover:bg-[var(--color-surface-hover)]"
            >
              <ChevronLeftIcon
                className="w-5 h-5"
                style={{ color: 'var(--color-text-muted)' }}
              />
            </button>
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-transparent outline-none text-sm"
            style={{ color: 'var(--color-text-primary)' }}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd
            className="text-[10px] px-1.5 py-0.5 rounded border font-mono"
            style={{
              color: 'var(--color-text-muted)',
              borderColor: 'var(--color-border)',
            }}
          >
            {view === 'main' ? 'ESC' : '← BACK'}
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <div
              className="px-4 py-8 text-center text-sm"
              style={{ color: 'var(--color-text-muted)' }}
            >
              {t('noMatches')}
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.group}>
                <div
                  className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-widest font-semibold flex items-center justify-between"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  <span>{g.group}</span>
                  {(g.group === t('groupProjects') || g.group === t('groupPrompts')) &&
                    g.items.length >= SEARCH_LIMIT && (
                      <span className="normal-case tracking-normal">
                        {t('showingFirst', { limit: SEARCH_LIMIT })}
                      </span>
                    )}
                </div>
                {g.items.map((item) => {
                  runningIndex += 1
                  const idx = runningIndex
                  const isActive = idx === activeIndex
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-idx={idx}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => selectIndex(idx)}
                      className="w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors"
                      style={{
                        background: isActive ? 'var(--color-surface-hover)' : 'transparent',
                        color: 'var(--color-text-primary)',
                        borderLeft: isActive
                          ? '3px solid var(--color-lime)'
                          : '3px solid transparent',
                      }}
                    >
                      <Icon className="w-4 h-4 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{item.name}</div>
                        {item.subtitle && (
                          <div
                            className="truncate text-xs"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        <div
          className="flex items-center justify-between px-4 py-2 text-[11px] border-t"
          style={{
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-muted)',
          }}
        >
          <div className="flex items-center gap-3">
            <span>
              <kbd className="font-mono">↑↓</kbd> {t('keyboardNavigate')}
            </span>
            <span>
              <kbd className="font-mono">↵</kbd> {t('keyboardOpen')}
            </span>
            <span>
              <kbd className="font-mono">esc</kbd> {t('keyboardClose')}
            </span>
          </div>
          <span>
            <kbd className="font-mono">⌘K</kbd> {t('keyboardToggle')}
          </span>
        </div>
      </div>
    </div>
      )}
    </>
  )
}
